import { assetUrl as appAssetUrl, localUrl as appLocalUrl } from '../app-path.js';
import common from './common';
import { CardForecastThreeDays } from './card-forecast-three-days';
import { trackGeolocationResult } from './geolocation-metrics';
import {
  recalcLocationCtaWidth,
  setLocationIconState,
  setupLocationCtaTag
} from './location-cta';

import {
  fetchWeatherJSON, geolocationPermission, weatherNumber, weatherText,
  weatherIcon, weatherQualityState, weatherCityUrls, setWeatherImage, reportWeatherUnavailable
} from './home-weather-client';

const setCurrentWeatherValue = (id, value, unit = '') => {
  const $field = $(`#current-weather-${id}`);
  if (value !== null) {
    $field.removeAttr('data-weather-unavailable').text(`${value}${unit}`);
    return;
  }
  $field.attr('data-weather-unavailable', 'home-now-value');
  if (id === 'temperature') {
    // The outer 48px line keeps its geometry; only the missing-value label is smaller.
    $field.empty().append($('<span>', { class: 'weather-unavailable-label' }).text('Sem dados'));
  } else $field.text('Sem dados');
};

let activeRender;
let renderGeneration = 0;
let locationRequest = 0;

const beginRender = () => {
  activeRender?.controller.abort();
  const controller = new AbortController();
  const generation = ++renderGeneration;
  const context = {
    controller, signal: controller.signal, generation,
    isCurrent: () => generation === renderGeneration && !controller.signal.aborted
  };
  activeRender = context;
  return context;
};

// The shared CTA helpers schedule unguarded timers/frames. Keep these home writes
// within the generation, and await the text transition with the other children.
const setHomeLocationIcon = (element, state) => {
  const node = typeof element === 'string' ? document.getElementById(element) : element;
  if (state === 'enabled') node?.classList.remove('location-cta--no-transition');
  setLocationIconState(node, state);
};

const transitionHomeCityText = (id, text, context) => {
  const node = document.getElementById(id);
  if (!node || !context.isCurrent()) return Promise.resolve();
  if (node.textContent === text) {
    node.classList.remove('is-hidden');
    return Promise.resolve();
  }
  node.classList.add('is-hidden');
  return new Promise(resolve => {
    const finish = () => {
      clearTimeout(timer);
      context.signal.removeEventListener('abort', finish);
      if (context.isCurrent()) {
        node.textContent = text;
        node.classList.remove('is-hidden');
        const cta = node.closest('.location-cta');
        if (cta) recalcLocationCtaWidth(cta);
      }
      resolve();
    };
    const timer = setTimeout(finish, 140);
    context.signal.addEventListener('abort', finish, { once: true });
  });
};

const getRainDropsSrc = (precipitation) => {
  if (precipitation >= 30) {
    return Array(3).fill(appAssetUrl("/dist/images/gota-azul.png"));
  } else if (precipitation >= 10) {
    return [
      appAssetUrl("/dist/images/gota-azul.png"),
      appAssetUrl("/dist/images/gota-azul.png"),
      appAssetUrl("/dist/images/gota-cinza.png")
    ];
  } else if (precipitation > 0) {
    return [
      appAssetUrl("/dist/images/gota-azul.png"),
      appAssetUrl("/dist/images/gota-cinza.png"),
      appAssetUrl("/dist/images/gota-cinza.png")
    ];
  } else {
    return Array(3).fill(appAssetUrl("/dist/images/gota-cinza.png"));
  }
};
const getCookie = (name) => {
  const match = document.cookie.match(new RegExp(`${name}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
};

const setCookie = (name, value, options = {}) => {
  const {
    domain = window.location.hostname,
    path = '/',
    expires = ''
  } = options;

  let cookieStr = `${name}=${encodeURIComponent(value)}; path=${path}; domain=.${domain}`;
  if (expires) cookieStr += `; expires=${expires}`;
  document.cookie = cookieStr;
};

let alreadyRequested = false;
const GEOLOCATION_TIMEOUT_MS = 5000;
const GEOLOCATION_CACHE_MAX_AGE_MS = 5 * 60 * 1000;

const geolocation = {

  getUserLocation: (options = {}) => {
    const requestedTimeoutMs = Number(options?.timeoutMs);
    const timeoutMs = requestedTimeoutMs > 0
      ? requestedTimeoutMs
      : GEOLOCATION_TIMEOUT_MS;

    return new Promise((resolve, reject) => {
      let settled = false;
      let timeoutId;

      const settle = (callback, value) => {
        if (settled) return;

        settled = true;
        clearTimeout(timeoutId);
        options?.signal?.removeEventListener('abort', cancel);
        callback(value);
      };
      const cancel = () => settle(reject, new Error('Geolocalização cancelada.'));
      if (options?.signal?.aborted) {
        cancel();
        return;
      }
      options?.signal?.addEventListener('abort', cancel, { once: true });

      if (navigator.geolocation) {
        timeoutId = setTimeout(() => {
          settle(reject, new Error(`Tempo limite de geolocalização excedido (${timeoutMs}ms).`));
        }, timeoutMs);

        try {
          navigator.geolocation.getCurrentPosition(
            position => settle(resolve, position),
            error => settle(reject, error),
            { timeout: timeoutMs, maximumAge: GEOLOCATION_CACHE_MAX_AGE_MS }
          );
          localStorage.setItem('requestedLocation', 'true');
        } catch (error) {
          settle(reject, error);
        }
      } else {
        settle(reject, new Error("Geolocalização não é suportada por este navegador."));
      }
    });
  },
  userGeolocation: async (verifyRequested = false, options = {}) => {
    const method = typeof options === 'string' ? options : options?.method || 'page_view';
    if (!verifyRequested && (alreadyRequested || sessionStorage.getItem('__geo_user_location_requested'))) return;
    alreadyRequested = true;
    const request = ++locationRequest;
    const isCurrentRequest = () => request === locationRequest;
    let context = beginRender();
    let fallbackPromise;
    let fallbackContext;

    const startFallback = () => {
      if (!isCurrentRequest()) return Promise.resolve(null);
      if (!fallbackPromise || !fallbackContext.isCurrent()) {
        if (!context.isCurrent()) context = beginRender();
        fallbackContext = context;
        fallbackPromise = geolocation.renderFromCookiesOrCache(3477, 558, 'São Paulo', 'SP', context)
          .catch(() => null);
      }
      return fallbackPromise;
    };

    const permission = await geolocationPermission(context.signal);
    if (!isCurrentRequest()) return null;
    const cookieCity = getCookie('__geo_city');
    const cookieLocale = getCookie('__geo_locale');
    const cookieName = getCookie('__geo_name');
    const cookieUf = getCookie('__geo_uf');
    if (cookieCity && cookieLocale && cookieName && cookieUf && permission !== 'granted') {
      await geolocation.renderFromCookiesOrCache(cookieLocale, cookieCity, cookieName, cookieUf, context);
      if (!isCurrentRequest() || !context.isCurrent()) return null;
      sessionStorage.setItem('__geo_user_location_requested', 'true');
      return { idlocale: cookieLocale, idcity: cookieCity, city: cookieName, uf: cookieUf };
    }
    if (permission !== 'granted') startFallback();

    try {
      const position = await geolocation.getUserLocation({ signal: context.signal });
      if (!isCurrentRequest()) return null;
      // Real coordinates immediately supersede the fallback, including its children.
      context = beginRender();
      const formData = new URLSearchParams();
      formData.append('latitude', position.coords.latitude.toFixed(5));
      formData.append('longitude', position.coords.longitude.toFixed(5));
      const result = await fetchWeatherJSON(appLocalUrl('/json/minha-localizacao'), {
        method: 'POST', body: formData, signal: context.signal
      });
      if (!isCurrentRequest() || !context.isCurrent()) return null;
      const locationData = result?.data?.[0];
      if (!locationData?.idlocale || !locationData?.idcity || !weatherText(locationData?.city) || !weatherText(locationData?.uf)) {
        await startFallback();
        return null;
      }
      const { idlocale, idcity, city, uf } = locationData;
      await geolocation.renderFromCookiesOrCache(idlocale, idcity, city, uf, context);
      if (!isCurrentRequest() || !context.isCurrent()) return null;
      trackGeolocationResult({ method, status: 'granted', locationData, coords: position.coords });
      $('#questionStep').css('display', 'flex');
      $('#useMyCurrentLocationStep').css('display', 'none');
      return { idlocale, idcity, city, uf };
    } catch (error) {
      if (!isCurrentRequest() || !context.isCurrent()) return null;
      $('#useMyCurrentLocationStep').css('display', 'flex');
      $('#questionStep').css('display', 'none');
      trackGeolocationResult({ method, status: 'denied' });
      // A timed out real-location lookup may have aborted the first fallback.
      if (fallbackContext && !fallbackContext.isCurrent()) context = beginRender();
      await startFallback();
      return null;
    }
  },

  hasAlreadyRendered: (idlocale, idcity) => {
    const cache = sessionStorage.getItem('__geo_last_render');
    console.warn("Utilizando dados de localização em cache");
    if (!cache) return false;

    try {
      const { lastLocale, lastCity, timestamp } = JSON.parse(cache);
      const fifteenMinutes = 15 * 60 * 1000;
      const now = Date.now();

      const isSameLocation = lastLocale === String(idlocale) && lastCity === String(idcity);
      const isRecent = timestamp && (now - timestamp < fifteenMinutes);

      return isSameLocation && isRecent;
    } catch {
      return false;
    }
  },

  setRenderCache: (idlocale, idcity) => {
    sessionStorage.setItem('__geo_last_render', JSON.stringify({
      lastLocale: String(idlocale),
      lastCity: String(idcity),
      timestamp: Date.now()
    }));
  },

  updateSidebarWeatherLinks: (idcity, cityName, uf) => {
    const dynamicLinks = document.querySelectorAll('a[data-category="previsao-tempo"]');
    if (!dynamicLinks.length) {
      return;
    }

    const citySlug = `${common.createSlug(cityName)}-${String(uf).toLowerCase()}`;

    dynamicLinks.forEach(link => {
      const templateHref = link.getAttribute('data-href-template') || link.getAttribute('href') || '';

      if (!templateHref.includes('{id}')) {
        return;
      }

      const newHref = templateHref
        .replace('{id}', String(idcity))
        .replace('{cidade}-{sigla-estado}', citySlug);

      link.setAttribute('href', newHref);
    });
  },

  renderFromCookiesOrCache: async (idlocale, idcity, cityName, uf, context) => {
    if (!context) {
      ++locationRequest;
      context = beginRender();
    }
    if (!context.isCurrent()) return false;
    const knownCity = weatherText(cityName);
    const knownUf = weatherText(uf);
    $('#current-weather-city').text(knownCity ? `${knownCity}${knownUf ? `, ${knownUf}` : ''}` : 'Localidade');
    const results = await geolocation.runWeatherRequestsInParallel(idlocale, idcity, context);
    if (!context.isCurrent()) return false;
    const success = results.every(result => result.status === 'fulfilled' && result.value === true);
    if (success) geolocation.setRenderCache(idlocale, idcity);
    else sessionStorage.removeItem('__geo_last_render');
    common.dynamicLink(idcity, cityName, uf);
    geolocation.updateSidebarWeatherLinks(idcity, cityName, uf);
    $('[data-id=geolocated]').removeClass('-hidden-geolocated');
    $('[data-id=geolocated] .city').text(`${cityName}, ${uf}`);
    return success;
  },

  runWeatherRequestsInParallel: async (idlocale, idcity, context = beginRender()) => {
    // Share daily transport without coupling it to current-weather availability.
    const forecastPromise = fetchWeatherJSON(
      appLocalUrl(`/json/myclimatempo/user/forecastFiveDays?idlocale=${idlocale}`),
      { method: 'POST', signal: context.signal }
    );
    return Promise.allSettled([
      geolocation.renderWeatherNowByGeolocation(idlocale, idcity, context),
      geolocation.updateDaysCards(idlocale, context, forecastPromise),
      CardForecastThreeDays.load(idlocale, null, null, { context, forecastPromise }),
      geolocation.updateNearbyBeaches(idlocale, context),
      geolocation.updateRainForecastChart(idlocale, idcity, context)
    ]);
  },

  renderWeatherNowByGeolocation: async (idlocale, idcity, context = beginRender()) => {
    const $card = $('div[data-current-weather]');
    const markUnavailable = () => {
      if (!context.isCurrent()) return;
      if ($card.length) reportWeatherUnavailable('now', context);
      $card.attr({ 'data-weather-state': 'unavailable', 'aria-busy': 'false' });
      $card.find('.shimmer-placeholder').removeClass('shimmer-placeholder');
      ['temperature', 'sensation', 'humidity', 'wind', 'pressure'].forEach(id => setCurrentWeatherValue(id, null));
      $('#current-weather-condition').text('Dados indisponíveis');
      $('#current-weather-updated-at').text('');
      $('#current-weather-icon').empty();
      $('#current-weather-city-header').removeClass('shimmer-placeholder');
      $('#city-name-card').removeClass('shimmer-placeholder');
      $('[data-id=geolocated] .temperature').text('Sem dados');
      setWeatherImage($('[data-id=geolocated] .ico'), '');
      $card.find('.card-current-weather__unavailable').addClass('_none');
    };

    if (!context.isCurrent()) return false;
    $card.attr({ 'data-weather-state': 'loading', 'aria-busy': 'true' });
    $card.find('.card-current-weather__unavailable').addClass('_none');
    try {
      const result = await fetchWeatherJSON(appLocalUrl(`/json/myclimatempo/user/weatherNow?idlocale=${idlocale}`), { method: 'POST', signal: context.signal });
      if (!context.isCurrent()) return false;
      const dataPath = result?.data?.getWeatherNow?.[0]?.data?.[0];
      const locale = dataPath?.locale;
      const weather = dataPath?.weather || {};
      const urls = weatherCityUrls(locale);
      const temperature = weatherNumber(weather.temperature);
      const icon = weatherIcon(weather.icon);
      const numericFields = ['temperature', 'sensation', 'humidity', 'pressure', 'windVelocity'];
      const usable = numericFields.some(field => weatherNumber(weather[field]) !== null) || !!icon || !!weatherText(weather.condition);
      const state = weatherQualityState(result, usable, temperature !== null && !!icon);
      if (!urls || state === 'unavailable' || (locale.idlocale && String(locale.idlocale) !== String(idlocale))) {
        markUnavailable();
        return false;
      }
      const permission = await geolocationPermission(context.signal);
      if (!context.isCurrent()) return false;
      const { cityName, cityUrl, todayUrl, tomorrowUrl, city15DaysUrl } = urls;
      if ($card.length && state === 'success') {
        setCookie('__geo_locale', idlocale);
        setCookie('__geo_city', locale.idcity);
        setCookie('__geo_name', locale.city);
        setCookie('__geo_uf', locale.uf);
      }

      $('[data-id=geolocated]').removeClass('-hidden-geolocated');
      $('[data-id=geolocated] .city').text(cityName);
      $('[data-id=geolocated] .temperature').text(temperature === null ? 'Sem dados' : `${temperature}°`);
      setWeatherImage($('[data-id=geolocated] .ico'), icon, cityName, true);
      const basePath = appLocalUrl('/previsao-do-tempo/cidade/');
      $('[data-id=geolocated] .link').attr('href', `${basePath}${cityUrl}`);

      const transitions = [];
      if ($card.length) {
        if (icon) {
          $('#current-weather-icon').empty().append($('<img>', {
            src: appAssetUrl(`/dist/images/v2/svg/${icon}.svg`), alt: `Ícone do tempo agora em ${cityName}`, width: 40, height: 40
          }));
        } else $('#current-weather-icon').empty();
        $('#current-weather-city').text(cityName);
        const fields = [
          ['temperature', 'temperature', '°'], ['sensation', 'sensation', '°'],
          ['humidity', 'humidity', '%'], ['pressure', 'pressure', ' hPa'],
          ['wind', 'windVelocity', 'km/h']
        ];
        fields.forEach(([id, field, unit]) => {
          const value = weatherNumber(weather[field]);
          setCurrentWeatherValue(id, value, unit);
        });
        $('#current-weather-condition').text(weatherText(weather.condition) || 'Dados indisponíveis');
        $('#current-weather-updated-at').text(weatherText(weather.dateUpdate));
        $('[data-id=Card_TempoAgora_Button_Today]').attr('href', todayUrl);
        $('[data-id=Card_TempoAgora_Button_Tomorrow]').attr('href', tomorrowUrl);
        $('[data-id=Card_TempoAgora_Button_15days]').attr('href', city15DaysUrl);
        $card.find('.shimmer-placeholder').removeClass('shimmer-placeholder');
        $card.find('.card-current-weather__unavailable').addClass('_none');
        $card.attr({ 'data-weather-state': state, 'aria-busy': 'false' });
        const enabled = permission === 'granted';
        const headerLink = setupLocationCtaTag({
          elementId: 'current-weather-link', isEnabled: enabled, href: todayUrl,
          navigationDataId: 'Bar_Highlights_Link_Location', actionDataId: 'Bar_Highlights_Link_UseLocation'
        });
        const cityLink = setupLocationCtaTag({
          elementId: 'city-btn-card', isEnabled: enabled, href: todayUrl,
          navigationDataId: 'Card_RainForecast_Link_Location', actionDataId: 'Card_RainForecast_Link_UseLocation'
        });
        setHomeLocationIcon(headerLink, enabled ? 'enabled' : 'disabled');
        setHomeLocationIcon(cityLink, enabled ? 'enabled' : 'disabled');
        transitions.push(transitionHomeCityText('current-weather-city-header', cityName, context));
        transitions.push(transitionHomeCityText('city-name-card', locale.city, context));
        $('#city-name-card').attr({ title: `Ver a previsão completa para ${locale.city}`, 'aria-label': `Ver a previsão completa para ${locale.city} - ${locale.uf}` });
        recalcLocationCtaWidth(headerLink);
        recalcLocationCtaWidth(cityLink);
      }
      geolocation.errorHitCurrentWeather(weather, context);
      await Promise.all(transitions);
      return context.isCurrent() && state === 'success';
    } catch (error) {
      markUnavailable();
      return false;
    }
  },

  updateDaysCards: async (idlocale, context = beginRender(), forecastPromise) => {
    const clearCards = () => {
      if (!context.isCurrent()) return;
      if ($('#day-forecast-card-icon-1').length) reportWeatherUnavailable('calendar', context);
      for (let index = 1; index <= 3; index++) {
        setWeatherImage($(`#day-forecast-card-icon-${index}`), '');
        $(`#forecast-card-min-temp-${index}, #forecast-card-max-temp-${index}, #forecast-card-rain-${index}`).text('Sem dados');
        $(`#forecast-card-rain-drops-wrapper-${index}`).empty();
      }
    };
    try {
      const result = await (forecastPromise || fetchWeatherJSON(
        appLocalUrl(`/json/myclimatempo/user/forecastFiveDays?idlocale=${idlocale}`),
        { method: 'POST', signal: context.signal }
      ));
      if (!context.isCurrent()) return false;
      const rows = Array.isArray(result?.data?.getFiveDaysForecast) ? result.data.getFiveDaysForecast : [];
      if (weatherQualityState(result, rows.length > 0) === 'unavailable') {
        clearCards();
        return false;
      }
      const urls = weatherCityUrls(result?.infoCity);
      if (urls) {
        $('#Botao_card_previsao_1').attr('href', urls.todayUrl);
        $('#Botao_card_previsao_2').attr('href', urls.tomorrowUrl);
        $('#Botao_card_previsao_3').attr('href', urls.city15DaysUrl);
      }
      let complete = true;
      let usable = false;
      for (let index = 0; index < 3; index++) {
        const forecast = rows[index] || {};
        const precipitation = weatherNumber(forecast.rain?.precipitation);
        const probability = weatherNumber(forecast.rain?.probability);
        const min = weatherNumber(forecast.temperature?.min);
        const max = weatherNumber(forecast.temperature?.max);
        const icon = weatherIcon(forecast.textIcon?.icon?.day);
        usable = usable || min !== null || max !== null || precipitation !== null || probability !== null || !!icon;
        const day = index + 1;
        const $wrapper = $(`#forecast-card-rain-drops-wrapper-${day}`).empty();
        if (precipitation !== null) getRainDropsSrc(precipitation).forEach((src, i) => {
          $wrapper.append($('<img>', { class: 'lazyload _width-auto', src, alt: `Gota ${i + 1}`, width: 10, height: 10 }));
        });
        setWeatherImage($(`#day-forecast-card-icon-${day}`), icon, weatherText(forecast.textIcon?.text?.pt));
        $(`#forecast-card-min-temp-${day}`).text(min === null ? 'Sem dados' : `${min}°`);
        $(`#forecast-card-max-temp-${day}`).text(max === null ? 'Sem dados' : `${max}°`);
        $(`#forecast-card-rain-${day}`).text(`${precipitation === null ? 'Sem dados' : precipitation + 'mm'} - ${probability === null ? 'Sem dados' : probability + '%'}`);
        complete = complete && min !== null && max !== null && !!icon;
      }
      if (!usable) { clearCards(); return false; }
      return weatherQualityState(result, usable, complete) === 'success';
    } catch (error) {
      clearCards();
      return false;
    }
  },

  updateRainForecastChart: async (idlocale, idcity, context = beginRender()) => {
    const url = appLocalUrl(`/json/myclimatempo/user/rainForecastSevenDays?idlocale=${idlocale}`);
    try {
      const data = await fetchWeatherJSON(url, { signal: context.signal });
      if (!context.isCurrent()) return false;

      const days = Array.isArray(data?.rain) ? data.rain : [];
      const forecastDailyRain = days.map(day => {
        const r = day?.rain ?? day;
        return {
          date: r?.date ?? null,
          dayWeek: r?.dayWeek ?? null,
          precipitation: r?.precipitation ?? null,
          probability: r?.probability ?? null
        };
      });
      if (forecastDailyRain && forecastDailyRain.length) {
        document.dispatchEvent(new CustomEvent('rainForecastReady', {
          detail: { forecastDailyRain, generation: context.generation }
        }));
      }
      return forecastDailyRain.length > 0;
    } catch (error) {
      return false;
    }
  },

  renderRainForecastList: (list = []) => {
    const $container = $('#rain-forecast-list');
    if (!$container.length || !Array.isArray(list) || list.length === 0) return;

    const rows = list.map((forecast) => {
      const precipitation = Number(
        (forecast && forecast.rain && forecast.rain.precipitation) ?? forecast?.precipitation ?? 0
      );
      const rainPercentage = Math.round(precipitation);

      let intensity = 'medium';
      if (rainPercentage >= 75) intensity = 'very-high';
      else if (rainPercentage >= 50) intensity = 'high';
      else if (rainPercentage >= 25) intensity = 'medium';

      const dateVal = forecast?.date ?? '';
      const civil = /^(\d{4})-(\d{2})-(\d{2})(?:$|[T ])/.exec(String(dateVal));
      const date = civil ? new Date(Date.UTC(Number(civil[1]), Number(civil[2]) - 1, Number(civil[3]))) : null;
      const isValid = civil && date.getUTCFullYear() === Number(civil[1])
        && date.getUTCMonth() + 1 === Number(civil[2]) && date.getUTCDate() === Number(civil[3]);
      // Derive both labels from the same civil date, never a separately supplied
      // weekday or a browser-local interpretation of midnight UTC.
      const dayWeek = isValid ? ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'][date.getUTCDay()] : '';
      const display = isValid ? `${civil[3]}/${civil[2]}` : `${dateVal}`;
      const datetime = isValid ? `${civil[1]}-${civil[2]}-${civil[3]}` : `${dateVal}`;

      const precRound = Math.round(precipitation);

      return `
        <div class="rain-row _flex _align-center _space-between _margin-b-15 -gray-dark-2 -font-12" role="row">
          <span class="metrics-rain-text" role="cell" aria-label="Volume de chuva: ${precRound} milímetros">
            <span>${precRound}</span>
            <span>mm</span>
          </span>
          <div class="rain-progress rain-progress-cell" aria-label="Nível de precipitação: ${precRound}mm de ${rainPercentage}%" role="meter" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${rainPercentage}" data-tooltip="${rainPercentage}% de precipitação (${precRound}mm)" tabindex="0" title="${rainPercentage}% de precipitação (${precRound}mm)">
            <span style="--rain-progress-width: ${rainPercentage}%;" data-intensity="${intensity}" aria-hidden="true"></span>
          </div>
          <div class="_center rain-date-cell" role="cell">
            <span class="metrics-rain-text" aria-label="Dia da semana: ${dayWeek}">${dayWeek}</span>
            <time class="metrics-rain-text _block" datetime="${datetime}" aria-label="Data: ${display}">${display}</time>
          </div>
        </div>
      `;
    }).join('');

    $container.html(rows);
  },

  updateNearbyBeaches: async (idlocale, context = beginRender()) => {
    try {
      const url = appLocalUrl(`/json/myclimatempo/user/nearbyBeaches?idlocale=${idlocale}`);
      const beaches = await fetchWeatherJSON(url, { method: 'POST', signal: context.signal });
      if (!context.isCurrent()) return false;

      // Suporte a dois layouts: UL antigo (#nearby-beaches-card-ul) e markup atual (.content-nearby-beaches)
      const $ulContainer = $("#nearby-beaches-card-ul");
      const $divContainer = $(".content-nearby-beaches");

      // Seleciona o container disponível
      const $container = $ulContainer.length ? $ulContainer : $divContainer;
      if (!$container.length) return true;
      $container.empty();

      if (!Array.isArray(beaches) || beaches.length === 0) {
        if ($ulContainer.length) {
          $container.append('<li>Nenhuma praia próxima encontrada.</li>');
        } else {
          $container.append('<p class="-gray">Nenhuma praia próxima encontrada.</p>');
        }
        return Array.isArray(beaches);
      }

      beaches.forEach((beach, idx) => {
        const prec = parseFloat(beach?.weather?.rain?.precipitation ?? 0);
        const prob = parseFloat(beach?.weather?.rain?.probability ?? 0);
        const min = beach?.weather?.temperature?.min ?? '-';
        const max = beach?.weather?.temperature?.max ?? '-';
        const icon = beach?.weather?.textIcon?.icon?.day ?? 'cloud';
        const iconAlt = beach?.weather?.textIcon?.text?.pt ?? '';
        const idbeach = beach?.info?.idbeach;
        const slug = beach?.info?.slugBeach;
        const uf = (beach?.info?.uf || '').toLowerCase();
        const name = beach?.info?.beach || '';

        const rainDropsSrc = getRainDropsSrc(prec);

        if ($ulContainer.length) {
          // Layout antigo baseado em <ul><li>
          const rainDrops = rainDropsSrc.map(src =>
            `<img class="_width-10 lazyload" data-src="${src}" width="10" height="10" alt="">`
          ).join('');

          $container.append(`
            <li class="item _padding-lr-10 _flex _align-center _space-between${idx !== 0 ? ' _padding-t-20' : ''}${idx !== beaches.length - 1 ? ' _padding-b-20 _border-b-light-1' : ''}">
              <div class="_flex _align-center">
                <img class="lazyload _margin-r-20" data-src="${appAssetUrl(`/dist/images/v2/svg/${icon}.svg`)}" width="33" height="33" alt="${iconAlt}" />
                <div>
                  <a href="${appLocalUrl(`/previsao-do-tempo/litoral/${idbeach}/${slug}-${uf}`)}" class="-bold -gray-dark-2 _margin-b-5 _block">${name}</a>
                  <p class="-gray _flex _align-center">
                    <img src="${appAssetUrl(`/dist/images/v2/svg/ic-arrow-min.svg`)}" class="_margin-r-5 _margin-b-3" width="12" height="12" alt="mínima">
                    ${min}
                    <img src="${appAssetUrl(`/dist/images/v2/svg/ic-arrow-max.svg`)}" class="_margin-l-10 _margin-r-5 _margin-b-3" width="12" height="12" alt="máxima">
                    ${max}
                  </p>
                  <p class="-gray _flex _align-center">${rainDrops} ${prec}mm - ${prob}%</p>
                </div>
              </div>
            </li>
          `);
        } else {
          // Layout atual: ancora .item-beach
          const drops = rainDropsSrc.map((src, i) =>
            `<img class="_width-10${i === 2 ? ' _margin-r-5' : ''} lazyload" width="10" height="10" data-src="${src}" alt="">`
          ).join('');

          $container.append(`
            <a href="${appLocalUrl(`/previsao-do-tempo/litoral/${idbeach}/${slug}-${uf}`)}" class="item-beach">
              <div class="item-beach-icon">
                <img src="${appAssetUrl(`/dist/images/v2/svg/${icon}.svg`)}" width="32" height="32" loading="lazy" alt="${iconAlt}">
              </div>
              <div class="item-beach-info">
                <div class="item-beach-city">
                  <p>${name}</p>
                </div>
                <div class="item-beach-weather">
                  <p class="item-beach-weather-temp-min">${min}°</p>
                  <p class="item-beach-weather-temp-max">${max}°</p>
                  ${drops}
                  <p class="item-beach-rain">${prec}mm - ${prob}%</p>
                </div>
              </div>
            </a>
          `);
        }
      });
      return true;
    } catch (error) {
      return false;
    }
  },

  errorHitCurrentWeather: (weather, context) => {
    if (context && !context.isCurrent()) return;
    if (!weatherIcon(weather.icon)) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'error_hit', name: 'data_aggregator_now_icon' });
    }
    if (weatherNumber(weather.temperature) === null) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: 'error_hit', name: 'data_aggregator_now_temperature' });
    }
  },
};

export default geolocation;
 
// Re-render rain forecast card when data becomes available
document.addEventListener('rainForecastReady', (event) => {
  try {
    if (event?.detail?.generation !== undefined && event.detail.generation !== renderGeneration) return;
    const list = event?.detail?.forecastDailyRain || [];
    if (!Array.isArray(list) || list.length === 0) return;
    geolocation.renderRainForecastList(list);
  } catch (e) {
    console.error('Falha ao renderizar previsão de chuva (evento):', e);
  }
});
